import type { UserSettingsHook } from '@hooks/settings'
import type { IRowNode } from 'ag-grid-community'
import dayjs from 'dayjs'
import { getLocalStorage, setLocalStorage } from './general'
import type {
  GridView,
  ViewPayload,
} from '@gravitate-js/excalibrr/dist/components/GraviGrid/CustomSidebarPanels/ConfigurableGridViews/components/schema.types'
export function getStoreNumberComparator<T>(key: string) {
  return (_a: unknown, _b: unknown, nodeA: IRowNode<T>, nodeB: IRowNode<T>) => {
    // For grouped rows, we need to compare the group keys
    const valueA = nodeA?.group ? nodeA.key : nodeA?.data?.[key]
    const valueB = nodeB?.group ? nodeB.key : nodeB?.data?.[key]

    if (!valueA && !valueB) {
      return 0
    }
    if (!valueA) {
      return 1
    }
    if (!valueB) {
      return -1
    }
    return compareStoreNumbers(valueA, valueB)
  }
}
// Sort stores storeSort sortStore sortStores
export function compareStoreNumbers(a, b) {
  const isNumeric = (str) => !isNaN(str)

  // Check if both are numeric
  if (isNumeric(a) && isNumeric(b)) {
    return Number(a) - Number(b)
  }

  // If 'a' is numeric and 'b' is not, 'a' comes first
  if (isNumeric(a)) {
    return -1
  }

  // If 'b' is numeric and 'a' is not, 'b' comes first
  if (isNumeric(b)) {
    return 1
  }

  // If neither is numeric, sort alphabetically
  return a.localeCompare(b)
}

export function getDefaultDateComparator(format?: string) {
  return (valueA: string, valueB: string, _: unknown, __: unknown) => {
    const dateA = dayjs(valueA, format)
    const dateB = dayjs(valueB, format)
    return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0
  }
}

export async function saveGridView(
  viewPayload: ViewPayload,
  storageKey: string,
  updateSetting: UserSettingsHook['updateSetting']
) {
  const key = `gridViewList::${storageKey}`
  // Get existing grid views
  const gridViewList = (getLocalStorage(key) || []) as GridView[]

  // Build new grid view
  const id = crypto.randomUUID()
  const newGridView = { ...viewPayload, id } as GridView

  // Update or insert
  const updatedViews = gridViewList.map((view) => (view.name === newGridView.name ? newGridView : view))
  if (!updatedViews.find((view) => view.name === newGridView.name)) {
    updatedViews.push(newGridView)
  }
  // Save to local storage and update setting
  try {
    const gridViewListString = JSON.stringify(updatedViews)
    await updateSetting(key, gridViewListString)
    setLocalStorage(key, gridViewListString)
  } catch (error) {
    console.error('Error saving grid view:', error)
  }
  return updatedViews
}

export function deleteGridView(
  gridView: ViewPayload,
  storageKey: string,
  updateSetting: UserSettingsHook['updateSetting']
) {
  const key = `gridViewList::${storageKey}`
  // Get existing grid views
  const gridViewList = (getLocalStorage(key) || []) as GridView[]

  // If found, delete
  const viewIndex = gridViewList.findIndex((view) => view.name === gridView.name)
  if (viewIndex !== -1) {
    gridViewList.splice(viewIndex, 1)
  } else {
    console.error(`Unable to delete. Grid view with name (${gridView.name}) not found.`)
  }

  try {
    const gridViewListString = JSON.stringify(gridViewList)
    updateSetting(key, gridViewListString)
    setLocalStorage(key, gridViewListString)
  } catch (error) {
    console.error('Error deleting grid view:', error)
  }
  return gridViewList
}

export function gridViewManager(
  storageKey: string,
  updateSetting: UserSettingsHook['updateSetting']
): {
  gridViewManager: {
    saveView: (viewPayload: ViewPayload) => Promise<GridView[]>
    deleteView: (gridView: ViewPayload) => GridView[]
  }
} {
  return {
    gridViewManager: {
      saveView: async (viewPayload: ViewPayload) => {
        return await saveGridView(viewPayload, storageKey, updateSetting)
      },
      deleteView: (gridView: ViewPayload) => {
        return deleteGridView(gridView, storageKey, updateSetting)
      },
    },
  }
}


export function getComparator(a: string, b: string, type: 'number' | 'string' | 'date' | 'time' | 'site') {
  switch (type) {
    case 'number':
      return Number(a) - Number(b)
    case 'string':
      return a.localeCompare(b)
    case 'date':
      return dayjs(a).valueOf() - dayjs(b).valueOf()
    case 'site': {
      const siteA = a.split(' - ')[0]
      const siteB = b.split(' - ')[0]
      return Number(siteA) - Number(siteB)
    }
    case 'time': {
      const format = 'h:mm A z'
      const timeA = dayjs(a, format)
      const timeB = dayjs(b, format)
      return timeA.valueOf() - timeB.valueOf()
    }
  }
}

const TIME_FILTER_OPTIONS = [
  'equals',
  'notEqual',
  'lessThan',
  'lessThanOrEqual',
  'greaterThan',
  'greaterThanOrEqual',
] as const

// Custom filter params for time columns e.g. BOL Time in Supplier Rec
// Enables less than, greater than, etc. filters for time values
export function getDefaultTimeFilterParams() {
  return {
    filterOptions: TIME_FILTER_OPTIONS,
    textCustomComparator: (filter: (typeof TIME_FILTER_OPTIONS)[number], value: string, filterText: string) => {
      const baseDate = '2000-01-01'
      const filterHasAMPM = /[AP]\.?M\.?/i.test(filterText)
      const valueHasAMPM = /[AP]\.?M\.?/i.test(value)
      const filterFormat = filterHasAMPM ? 'h:mm A' : 'H:mm'
      const valueFormat = valueHasAMPM ? 'h:mm A' : 'H:mm'
      const valueTime = dayjs(`${baseDate} ${value.toUpperCase()}`, `YYYY-MM-DD ${valueFormat}`).valueOf()
      const filterTime = dayjs(`${baseDate} ${filterText.toUpperCase()}`, `YYYY-MM-DD ${filterFormat}`).valueOf()

      const comparisons: Record<(typeof TIME_FILTER_OPTIONS)[number], boolean> = {
        equals: valueTime === filterTime,
        notEqual: valueTime !== filterTime,
        lessThan: valueTime < filterTime,
        lessThanOrEqual: valueTime <= filterTime,
        greaterThan: valueTime > filterTime,
        greaterThanOrEqual: valueTime >= filterTime,
      }
      return comparisons[filter] ?? false
    },
  }
}
